// src/components/Sidebar/LayersPanel/LayersPanel.tsx
import React, { useMemo } from 'react';
import { Eye, EyeOff, Lock, Unlock, Trash2, Copy, GripVertical } from 'lucide-react';
import { useScene } from 'src/hooks/useScene';
import { useModelStore } from 'src/stores/modelStore';
import { useUiStateStore } from 'src/stores/uiStateStore';
import {
  getAllLayers,
  getLayerIcon,
  getLayerIconColor,
  LayerType,
  LayerMetadata,
} from 'src/features/layers';
import * as LucideIcons from 'lucide-react';
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  DragEndEvent,
} from '@dnd-kit/core';
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';

interface SortableLayerItemProps {
  layer: LayerMetadata;
  selected: boolean;
  onToggleVisibility: (id: string, type: LayerType) => void;
  onToggleLock: (id: string, type: LayerType) => void;
  onDuplicate: (id: string, type: LayerType) => void;
  onDelete: (id: string, type: LayerType) => void;
  onSelect: (id: string, type: LayerType) => void;
}

const SortableLayerItem: React.FC<SortableLayerItemProps> = ({
  layer,
  selected,
  onToggleVisibility,
  onToggleLock,
  onDuplicate,
  onDelete,
  onSelect,
}) => {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: layer.id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  };

  const IconComponent = (() => {
    const iconName = getLayerIcon(layer.type);
    return (LucideIcons as any)[iconName] || LucideIcons.Layers;
  })();

  const iconColor = getLayerIconColor(layer.type);

  return (
    <div
      ref={setNodeRef}
      style={style}
      onClick={() => onSelect(layer.id, layer.type)}
      className={`group flex items-center gap-2 rounded-lg border p-2 transition-all cursor-pointer ${
        selected
          ? 'border-blue-500 bg-blue-50 dark:bg-blue-900/20'
          : 'border-transparent hover:bg-gray-50 dark:hover:bg-slate-800'
      }`}
    >
      {/* Drag Handle */}
      <button
        {...attributes}
        {...listeners}
        className="cursor-grab text-gray-400 hover:text-gray-600 dark:text-gray-500 dark:hover:text-gray-300 active:cursor-grabbing"
        title="Drag to reorder"
      >
        <GripVertical className="h-4 w-4" />
      </button>

      {/* Type Icon */}
      <div className="flex-shrink-0">
        <IconComponent className="h-4 w-4" style={{ color: iconColor }} />
      </div>

      {/* Layer Name */}
      <div className="flex-1 truncate">
        <span className="text-sm font-medium text-gray-900 dark:text-gray-100">
          {layer.name}
        </span>
      </div>

      {/* Layer Controls */}
      <div className="flex items-center gap-1">
        {/* Visibility Toggle */}
        <button
          onClick={(e) => {
            e.stopPropagation();
            onToggleVisibility(layer.id, layer.type);
          }}
          className="rounded p-1 text-gray-500 transition-colors hover:bg-gray-200 dark:text-gray-400 dark:hover:bg-slate-700"
          title={layer.visible ? 'Hide layer' : 'Show layer'}
        >
          {layer.visible ? (
            <Eye className="h-4 w-4" />
          ) : (
            <EyeOff className="h-4 w-4 opacity-50" />
          )}
        </button>

        {/* Lock Toggle */}
        <button
          onClick={(e) => {
            e.stopPropagation();
            onToggleLock(layer.id, layer.type);
          }}
          className="rounded p-1 text-gray-500 transition-colors hover:bg-gray-200 dark:text-gray-400 dark:hover:bg-slate-700"
          title={layer.locked ? 'Unlock layer' : 'Lock layer'}
        >
          {layer.locked ? (
            <Lock className="h-4 w-4" />
          ) : (
            <Unlock className="h-4 w-4 opacity-50" />
          )}
        </button>

        {/* More Actions (visible on hover) */}
        <div className="flex items-center gap-1 opacity-0 transition-opacity group-hover:opacity-100">
          <button
            onClick={(e) => {
              e.stopPropagation();
              onDuplicate(layer.id, layer.type);
            }}
            className="rounded p-1 text-gray-500 transition-colors hover:bg-gray-200 dark:text-gray-400 dark:hover:bg-slate-700"
            title="Duplicate layer"
          >
            <Copy className="h-3.5 w-3.5" />
          </button>
          <button
            onClick={(e) => {
              e.stopPropagation();
              onDelete(layer.id, layer.type);
            }}
            className="rounded p-1 text-red-500 transition-colors hover:bg-red-100 dark:text-red-400 dark:hover:bg-red-900/20"
            title="Delete layer"
          >
            <Trash2 className="h-3.5 w-3.5" />
          </button>
        </div>
      </div>
    </div>
  );
};

export const LayersPanel: React.FC = () => {
  const scene = useScene();
  const modelItems = useModelStore((state) => state.items);
  const itemControls = useUiStateStore((state) => state.itemControls);
  const uiStateActions = useUiStateStore((state) => state.actions);

  // Get all layers from the current view (already sorted by zIndex)
  const layers = useMemo(() => {
    return getAllLayers(
      scene.items,
      scene.rectangles,
      scene.connectors,
      scene.textBoxes,
      modelItems
    );
  }, [scene.items, scene.rectangles, scene.connectors, scene.textBoxes, modelItems]);

  // Setup drag and drop sensors
  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  // Handle drag end - update zIndex in data model
  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;

    if (over && active.id !== over.id) {
      const oldIndex = layers.findIndex((item) => item.id === active.id);
      const newIndex = layers.findIndex((item) => item.id === over.id);

      // Calculate new zIndex values for reordered layers
      const reorderedLayers = arrayMove(layers, oldIndex, newIndex);

      // Update zIndex for each layer in the data model
      // Assign zIndex values in descending order (top layer = highest zIndex)
      reorderedLayers.forEach((layer, index) => {
        const newZIndex = reorderedLayers.length - index;

        // Update the zIndex in the data model based on layer type
        switch (layer.type) {
          case LayerType.ITEM:
            scene.updateViewItem(layer.id, { zIndex: newZIndex });
            break;
          case LayerType.RECTANGLE:
            scene.updateRectangle(layer.id, { zIndex: newZIndex });
            break;
          case LayerType.CONNECTOR:
            scene.updateConnector(layer.id, { zIndex: newZIndex });
            break;
          case LayerType.TEXTBOX:
            scene.updateTextBox(layer.id, { zIndex: newZIndex });
            break;
        }
      });
    }
  };

  // Handle layer visibility toggle
  const handleToggleVisibility = (id: string, type: LayerType) => {
    scene.toggleLayerVisibility(id, type);
  };

  // Handle layer lock toggle
  const handleToggleLock = (id: string, type: LayerType) => {
    scene.toggleLayerLocked(id, type);
  };

  // Handle layer deletion
  const handleDeleteLayer = (id: string, type: LayerType) => {
    switch (type) {
      case LayerType.ITEM:
        scene.deleteViewItem(id);
        break;
      case LayerType.RECTANGLE:
        scene.deleteRectangle(id);
        break;
      case LayerType.CONNECTOR:
        scene.deleteConnector(id);
        break;
      case LayerType.TEXTBOX:
        scene.deleteTextBox(id);
        break;
    }
  };

  // Handle layer duplication
  const handleDuplicateLayer = (id: string, type: LayerType) => {
    switch (type) {
      case LayerType.ITEM: {
        const item = scene.items.find((i) => i.id === id);
        if (item) {
          const newId = `item-${Date.now()}`;
          scene.createViewItem({
            id: newId,
            tile: {
              x: item.tile.x + 1,
              y: item.tile.y + 1
            },
            labelHeight: item.labelHeight,
            visible: item.visible,
            locked: false, // Unlock the duplicate
          });
        }
        break;
      }
      case LayerType.RECTANGLE: {
        const rect = scene.rectangles.find((r) => r.id === id);
        if (rect) {
          scene.createRectangle({
            ...rect,
            id: `rect-${Date.now()}`,
            from: {
              x: rect.from.x + 1,
              y: rect.from.y + 1
            },
            to: {
              x: rect.to.x + 1,
              y: rect.to.y + 1
            },
            locked: false, // Unlock the duplicate
          });
        }
        break;
      }
      case LayerType.CONNECTOR: {
        const conn = scene.connectors.find((c) => c.id === id);
        if (conn) {
          scene.createConnector({
            ...conn,
            id: `conn-${Date.now()}`,
            locked: false, // Unlock the duplicate
          });
        }
        break;
      }
      case LayerType.TEXTBOX: {
        const textBox = scene.textBoxes.find((t) => t.id === id);
        if (textBox) {
          scene.createTextBox({
            ...textBox,
            id: `text-${Date.now()}`,
            tile: {
              x: textBox.tile.x + 1,
              y: textBox.tile.y + 1
            },
            locked: false, // Unlock the duplicate
          });
        }
        break;
      }
    }
  };

  // Handle show all layers
  const handleShowAll = () => {
    scene.showAllLayers();
  };

  // Handle hide all layers
  const handleHideAll = () => {
    scene.hideAllLayers();
  };

  // Handle lock all layers
  const handleLockAll = () => {
    scene.lockAllLayers();
  };

  // Handle layer selection
  const handleSelectLayer = (id: string, type: LayerType) => {
    switch (type) {
      case LayerType.ITEM:
        uiStateActions.setItemControls({ type: 'ITEM', id });
        break;
      case LayerType.RECTANGLE:
        uiStateActions.setItemControls({ type: 'RECTANGLE', id });
        break;
      case LayerType.CONNECTOR:
        uiStateActions.setItemControls({ type: 'CONNECTOR', id });
        break;
      case LayerType.TEXTBOX:
        uiStateActions.setItemControls({ type: 'TEXTBOX', id });
        break;
    }
  };

  // Check if layer is selected
  const isLayerSelected = (layerId: string) => {
    if (!itemControls) return false;
    // ItemControls can be ItemReference (has id), AddItemControls, or MultiSelectControls
    // Only ItemReference has an id property
    return 'id' in itemControls && itemControls.id === layerId;
  };

  return (
    <div className="flex h-full flex-col">
      {/* Layer Controls Header */}
      <div className="flex items-center justify-between border-b border-gray-200 p-3 dark:border-slate-700">
        <span className="text-xs font-semibold uppercase text-gray-500 dark:text-gray-400">
          Layers ({layers.length})
        </span>
      </div>

      {/* Layers List */}
      <div className="flex-1 overflow-y-auto">
        {layers.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-12 text-center">
            <div className="mb-3 text-4xl">📐</div>
            <p className="text-sm text-gray-500 dark:text-gray-400">
              No layers yet
            </p>
            <p className="mt-1 text-xs text-gray-400 dark:text-gray-500">
              Add components to create layers
            </p>
          </div>
        ) : (
          <DndContext
            sensors={sensors}
            collisionDetection={closestCenter}
            onDragEnd={handleDragEnd}
          >
            <SortableContext
              items={layers.map((l) => l.id)}
              strategy={verticalListSortingStrategy}
            >
              <div className="space-y-1 p-2">
                {layers.map((layer) => (
                  <SortableLayerItem
                    key={layer.id}
                    layer={layer}
                    selected={isLayerSelected(layer.id)}
                    onToggleVisibility={handleToggleVisibility}
                    onToggleLock={handleToggleLock}
                    onDuplicate={handleDuplicateLayer}
                    onDelete={handleDeleteLayer}
                    onSelect={handleSelectLayer}
                  />
                ))}
              </div>
            </SortableContext>
          </DndContext>
        )}
      </div>

      {/* Layer Actions Footer */}
      <div className="border-t border-gray-200 p-3 dark:border-slate-700">
        <div className="grid grid-cols-3 gap-2 text-xs">
          <button
            onClick={handleShowAll}
            className="rounded border border-gray-200 px-2 py-1.5 font-medium text-gray-700 transition-colors hover:bg-gray-50 dark:border-slate-600 dark:text-gray-300 dark:hover:bg-slate-800"
          >
            Show All
          </button>
          <button
            onClick={handleHideAll}
            className="rounded border border-gray-200 px-2 py-1.5 font-medium text-gray-700 transition-colors hover:bg-gray-50 dark:border-slate-600 dark:text-gray-300 dark:hover:bg-slate-800"
          >
            Hide All
          </button>
          <button
            onClick={handleLockAll}
            className="rounded border border-gray-200 px-2 py-1.5 font-medium text-gray-700 transition-colors hover:bg-gray-50 dark:border-slate-600 dark:text-gray-300 dark:hover:bg-slate-800"
          >
            Lock All
          </button>
        </div>
      </div>
    </div>
  );
};
